import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import dayjs from "dayjs";
import L from "leaflet";
import {
  Building2,
  MapPin,
  PlusCircle,
  Loader2,
  RefreshCw,
  ServerCrash,
  PlugZap,
  Unplug,
  ChevronDown,
  ChevronUp,
  Network,
  Calendar,
  Settings,
  ExternalLink,
  BatteryCharging,
} from "lucide-react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import Api from "../../services/Api";
import { queryKeys } from "../../lib/query";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { useFleetData } from "../../lib/context/FleetDataProvider";
import useHierarchy from "../../lib/hooks/useHierarchy";
import DateRangeToolbar from "../fleet_dashboard/DateRangeToolbar";
import { RANGE_TYPE_LABELS } from "../../lib/utils/constants";
import { getRange } from "../../lib/utils/utility";
import "../fleet_dashboard/FleetDashboard.scss";
import "./StationDashboard.scss";

const UTILIZATION_URL = "/service/iqevse-csms/sessions/v1/station";
const SITE_UTILIZATION_URL = "/service/iqevse-csms/sessions/v1/site";
const SYSTEM_INFO_URL = "/service/systems";

/* ── Helper: compute station stats from sites[].chargers[] ── */
function computeStationStats(station) {
  const sites = station.sites || [];
  let totalChargers = 0;
  let online = 0;
  sites.forEach(site => {
    const chargers = site.chargers || [];
    totalChargers += chargers.length;
    chargers.forEach(c => {
      if (c.isConnected) online += 1;
    });
  });
  return { siteCount: sites.length, totalChargers, online };
}

/* ── Helper: compute site-level stats ── */
function computeSiteStats(site) {
  const chargers = site.chargers || [];
  let online = 0;
  chargers.forEach(c => {
    if (c.isConnected) online += 1;
  });
  return { total: chargers.length, online };
}

/* ── Extract lat/lng from systemInfo response ── */
function extractLatLng(info) {
  if (!info) return null;
  const loc = info.address?.location;
  if (loc && !Array.isArray(loc) && typeof loc === "object") {
    if (loc.latitude != null && loc.longitude != null)
      return { lat: parseFloat(loc.latitude), lng: parseFloat(loc.longitude) };
    if (loc.lat != null && (loc.lng != null || loc.lon != null))
      return { lat: parseFloat(loc.lat), lng: parseFloat(loc.lng ?? loc.lon) };
  }
  const geo = info.address?.geolocation;
  if (geo && typeof geo === "object" && !Array.isArray(geo)) {
    if (geo.latitude != null && geo.longitude != null)
      return { lat: parseFloat(geo.latitude), lng: parseFloat(geo.longitude) };
    if (geo.lat != null && (geo.lng != null || geo.lon != null))
      return { lat: parseFloat(geo.lat), lng: parseFloat(geo.lng ?? geo.lon) };
  }
  if (info.latitude != null && info.longitude != null)
    return { lat: parseFloat(info.latitude), lng: parseFloat(info.longitude) };
  return null;
}

/* ── Build readable address string ── */
function buildAddress(info) {
  if (!info) return "";
  const addr = info.address || {};
  return [
    addr.address1 || addr.street,
    addr.city,
    addr.state || addr.province,
    addr.country,
    addr.zip || addr.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
}

/* ══════════════════════════════════════════════════════════════
   MiniMap — readonly Leaflet map with zoom controls
   ══════════════════════════════════════════════════════════════ */
function MiniMap({ lat, lng, height = 160 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return;

    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: 14,
      scrollWheelZoom: false,
      dragging: false,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);
    L.circleMarker([lat, lng], { radius: 7, fillColor: "#f47c20", fillOpacity: 0.85, color: "#fff", weight: 2 }).addTo(
      map
    );
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [lat, lng]);

  if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return null;

  return <div ref={containerRef} className="a-minimap" style={{ height }} />;
}

MiniMap.propTypes = { lat: PropTypes.number, lng: PropTypes.number, height: PropTypes.number };

/* ══════════════════════════════════════════════════════════════
   Utilization KPI row (fetches per station)
   ══════════════════════════════════════════════════════════════ */
function UtilizationKpis({ stationId, startDate, endDate }) {
  const { data = null, isLoading: loading } = useQuery({
    queryKey: queryKeys.utilization.byStation(stationId, startDate, endDate),
    queryFn: () => {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return Api.post(`${UTILIZATION_URL}/${stationId}/utilization`, { startDate, endDate, timezone: tz });
    },
    enabled: Boolean(stationId && startDate && endDate),
    staleTime: 5 * 60 * 1000,
  });

  if (loading) {
    return (
      <div className="a-station__util a-station__util--loading">
        <Loader2 size={14} className="a-station__util-spinner" /> Loading utilization...
      </div>
    );
  }
  if (!data) return null;

  const kpis = [
    { label: "Sessions", value: data.totalSessions ?? "—" },
    { label: "Utilization", value: data.utilizationPercent != null ? `${data.utilizationPercent}%` : "—" },
    { label: "Cost", value: data.totalSessionsCost != null ? `$${data.totalSessionsCost}` : "—" },
    { label: "Total Time", value: data.totalUtilizationTimeFormatted || "—" },
  ];

  return (
    <div className="a-station__util-kpis">
      {kpis.map(kpi => (
        <div key={kpi.label} className="a-station__kpi">
          <div className="a-station__kpi-val">{kpi.value}</div>
          <div className="a-station__kpi-lbl">{kpi.label}</div>
        </div>
      ))}
    </div>
  );
}

UtilizationKpis.propTypes = {
  stationId: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};

/* ══════════════════════════════════════════════════════════════
   Main Dashboard
   ══════════════════════════════════════════════════════════════ */
export default function StationDashboard() {
  const navigate = useNavigate();
  const { stations: hierarchyStations, isLoading: loading, isError, refresh: refreshHierarchy } = useHierarchy();

  const [activePreset, setActivePreset] = useState(RANGE_TYPE_LABELS.DAY_7);
  const [dateRange, setDateRange] = useState(() => getRange(RANGE_TYPE_LABELS.DAY_7));

  const handlePresetChange = useCallback(value => {
    setActivePreset(value);
    setDateRange(getRange(value));
  }, []);
  const handleCustomChange = useCallback((range, type) => {
    setDateRange(range);
    setActivePreset(type);
  }, []);

  const startDate = useMemo(() => (dateRange?.[0] ? dayjs(dateRange[0]).format("DD-MM-YYYY") : ""), [dateRange]);
  const endDate = useMemo(() => (dateRange?.[1] ? dayjs(dateRange[1]).format("DD-MM-YYYY") : ""), [dateRange]);

  const error = isError ? "Failed to load stations" : null;

  const allStations = hierarchyStations;

  const stations = useMemo(
    () => allStations.filter(s => (s.stationName || s.name || "").toLowerCase() !== "unassigned"),
    [allStations]
  );
  const unassigned = useMemo(
    () => allStations.find(s => (s.stationName || s.name || "").toLowerCase() === "unassigned") || null,
    [allStations]
  );

  const unassignedStats = useMemo(() => {
    if (!unassigned) return { siteCount: 0, chargerCount: 0, onlineCount: 0 };
    const sites = unassigned.sites || [];
    let chargerCount = 0,
      onlineCount = 0;
    sites.forEach(site => {
      const c = site.chargers || [];
      chargerCount += c.length;
      c.forEach(ch => {
        if (ch.isConnected) onlineCount += 1;
      });
    });
    return { siteCount: sites.length, chargerCount, onlineCount };
  }, [unassigned]);

  const [unassignedOpen, setUnassignedOpen] = useState(false);

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="station-dashboard__state">
        <motion.div
          className="station-dashboard__state-icon station-dashboard__state-icon--loading"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 size={40} />
        </motion.div>
        <h3 className="station-dashboard__state-title">Loading Stations...</h3>
        <p className="station-dashboard__state-desc">Fetching station overview from server</p>
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="station-dashboard__state">
        <div className="station-dashboard__state-icon station-dashboard__state-icon--error">
          <ServerCrash size={40} />
        </div>
        <h3 className="station-dashboard__state-title">Something went wrong</h3>
        <p className="station-dashboard__state-desc">{error}</p>
        <button
          className="station-dashboard__state-btn station-dashboard__state-btn--secondary"
          onClick={refreshHierarchy}
        >
          <RefreshCw size={16} /> Retry
        </button>
      </div>
    );
  }

  /* ── Empty ── */
  if (stations.length === 0) {
    return (
      <motion.div
        className="station-dashboard__state"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="station-dashboard__state-icon station-dashboard__state-icon--empty">
          <Building2 size={48} />
        </div>
        <h3 className="station-dashboard__state-title">No Stations Found</h3>
        <p className="station-dashboard__state-desc">
          Get started by creating your first Charging Station. You can add multiple sites to create a Station.
        </p>
        <button
          className="station-dashboard__state-btn station-dashboard__state-btn--primary"
          onClick={() => navigate("/stations/manage?mode=create")}
        >
          <PlusCircle size={18} />
          Create Station
        </button>
      </motion.div>
    );
  }

  /* ── Data ── */
  return (
    <div className="station-dashboard">
      {/* <div className="fleet-overview-panel"> */}
      <DateRangeToolbar
        activePreset={activePreset}
        onPresetChange={handlePresetChange}
        onCustomChange={handleCustomChange}
      />
      {/* </div> */}

      <div className="station-dashboard__list">
        {stations.map((station, i) => (
          <StationCard
            key={station.stationId || station.id || i}
            station={station}
            index={i}
            startDate={startDate}
            endDate={endDate}
          />
        ))}

        {/* Unassigned Sites */}
        {unassigned && unassignedStats.siteCount > 0 && (
          <motion.div
            className="a-station a-station--unassigned"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + stations.length * 0.05 }}
          >
            <div className="a-station__header">
              <div>
                <div className="a-station__name">
                  <Unplug size={18} /> Unassigned Sites
                </div>
                <div className="a-station__meta">Sites not yet assigned to any station</div>
              </div>
            </div>
            <div className="a-station__kpis">
              <div className="a-station__kpi">
                <div className="a-station__kpi-val">{unassignedStats.siteCount}</div>
                <div className="a-station__kpi-lbl">Sites</div>
              </div>
              <div className="a-station__kpi">
                <div className="a-station__kpi-val">{unassignedStats.chargerCount}</div>
                <div className="a-station__kpi-lbl">Chargers</div>
              </div>
              <div className="a-station__kpi">
                <div className="a-station__kpi-val">{unassignedStats.onlineCount}</div>
                <div className="a-station__kpi-lbl">Online</div>
              </div>
            </div>
            <button className="a-station__expand-btn" onClick={() => setUnassignedOpen(prev => !prev)}>
              <Network size={13} />
              <span>{unassignedStats.siteCount} Sites</span>
              <ChevronDown
                size={14}
                className={`a-station__expand-chevron ${unassignedOpen ? "" : "a-station__expand-chevron--closed"}`}
              />
            </button>
            {unassignedOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.2 }}
              >
                <div className="a-sites-grid">
                  {(unassigned.sites || []).map(site => {
                    const sid = site.siteId || site.id;
                    const sName = site.siteName || site.displayName || site.name || `Site ${sid}`;
                    return (
                      <SiteCard
                        key={sid}
                        siteName={sName}
                        siteId={sid}
                        stats={computeSiteStats(site)}
                        chargers={site.chargers || []}
                        startDate={startDate}
                        endDate={endDate}
                      />
                    );
                  })}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Station Card
   ══════════════════════════════════════════════════════════════ */
function StationCard({ station, index, startDate, endDate }) {
  const stationId = station.stationId || station.id;
  const name = station.stationName || station.name || "Unnamed";
  const address = station.address || station.location || "";
  const createdAt = station.createdAt || "";
  const lat = parseFloat(station.latitude || station.lat);
  const lng = parseFloat(station.longitude || station.lng);
  const sites = station.sites || [];
  const stats = computeStationStats(station);

  return (
    <motion.div
      className="a-station"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.05 }}
    >
      {/* Header: name left, Manage Station link top-right */}
      <div className="a-station__header">
        <div>
          <h3 className="a-station__name">
            <PlugZap size={18} /> {name}
          </h3>
          {createdAt && (
            <div className="a-station__created">
              <Calendar size={12} /> Created {createdAt}
            </div>
          )}
        </div>
        <Link
          to={`/stations/manage/${stationId}`}
          className="a-station__manage-link"
          onClick={e => e.stopPropagation()}
        >
          <Settings size={13} /> Manage Station <ExternalLink size={11} />
        </Link>
      </div>

      {/* KPIs row */}
      <div className="a-station__kpis">
        <div className="a-station__kpi">
          <div className="a-station__kpi-val">{stats.siteCount}</div>
          <div className="a-station__kpi-lbl">Sites</div>
        </div>
        <div className="a-station__kpi">
          <div className="a-station__kpi-val">{stats.totalChargers}</div>
          <div className="a-station__kpi-lbl">Chargers</div>
        </div>
        <div className="a-station__kpi">
          <div className="a-station__kpi-val">{stats.online}</div>
          <div className="a-station__kpi-lbl">Online</div>
        </div>
      </div>

      {/* Utilization + Address/Map in same row */}
      <div className="a-station__detail-row">
        <div className="a-station__detail-col">
          <UtilizationKpis stationId={stationId} startDate={startDate} endDate={endDate} />
        </div>
        {(address || !isNaN(lat)) && (
          <div className="a-station__detail-col">
            {address && (
              <div className="a-station__address">
                <MapPin size={13} /> {address}
              </div>
            )}
            <MiniMap lat={lat} lng={lng} height={140} />
          </div>
        )}
      </div>

      {/* Sites Grid */}
      {sites.length > 0 && (
        <div className="a-sites-grid">
          {sites.map(site => {
            const sid = site.siteId || site.id;
            const sName = site.siteName || site.displayName || site.name || `Site ${sid}`;
            return (
              <SiteCard
                key={sid}
                siteName={sName}
                siteId={sid}
                stats={computeSiteStats(site)}
                chargers={site.chargers || []}
                startDate={startDate}
                endDate={endDate}
              />
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

StationCard.propTypes = {
  station: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};

/* ══════════════════════════════════════════════════════════════
   Site Card — with address + map from systemInfo API
   ══════════════════════════════════════════════════════════════ */
function SiteCard({ siteName, siteId, stats, chargers, startDate, endDate }) {
  const { data: siteInfo = null, isFetched: infoLoaded } = useQuery({
    queryKey: queryKeys.sites.systemInfo(siteId),
    queryFn: () => Api.get(`${SYSTEM_INFO_URL}/${siteId}/systeminfo`),
    enabled: Boolean(siteId),
    staleTime: 10 * 60 * 1000,
  });
  const [showAllChargers, setShowAllChargers] = useState(false);

  const CHARGER_DISPLAY_LIMIT = 4;
  const displayedChargers = showAllChargers ? chargers : chargers.slice(0, CHARGER_DISPLAY_LIMIT);
  const hasMoreChargers = chargers.length > CHARGER_DISPLAY_LIMIT;

  const siteAddress = siteInfo ? buildAddress(siteInfo) : "";
  const coords = siteInfo ? extractLatLng(siteInfo) : null;

  return (
    <div className="a-site">
      <div className="a-site__header">
        <span className="a-site__name">
          <MapPin size={12} /> {siteName}
        </span>
        <span className="a-site__badge">{siteId}</span>
      </div>
      <div className="a-site__kpis">
        <div>
          <div className="a-site__kpi-val">{stats.total}</div>
          <div className="a-site__kpi-lbl">Chargers</div>
        </div>
        <div>
          <div className="a-site__kpi-val">{stats.online}</div>
          <div className="a-site__kpi-lbl">Online</div>
        </div>
      </div>

      {/* Site utilization KPIs */}
      <SiteUtilizationKpis siteId={siteId} startDate={startDate} endDate={endDate} />

      {/* Site address + map */}
      {infoLoaded && (siteAddress || coords) && (
        <div className="a-site__location">
          {siteAddress && (
            <div className="a-site__address">
              <MapPin size={10} /> {siteAddress}
            </div>
          )}
          {coords && <MiniMap lat={coords.lat} lng={coords.lng} height={100} />}
        </div>
      )}

      {/* Chargers grid */}
      <div className="a-chargers">
        {displayedChargers.map(charger => (
          <ChargerTile
            key={charger.serialNumber || charger.chargerId || charger.id}
            charger={charger}
            siteId={siteId}
          />
        ))}
        {hasMoreChargers && (
          <button type="button" className="a-chargers__view-more" onClick={() => setShowAllChargers(!showAllChargers)}>
            {showAllChargers ? (
              <>
                <ChevronUp size={14} />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown size={14} />
                View More ({chargers.length - CHARGER_DISPLAY_LIMIT} more)
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

SiteCard.propTypes = {
  siteName: PropTypes.string.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  stats: PropTypes.object.isRequired,
  chargers: PropTypes.array.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};

/* ══════════════════════════════════════════════════════════════
   Site Utilization KPIs (fetches per site)
   ══════════════════════════════════════════════════════════════ */
function SiteUtilizationKpis({ siteId, startDate, endDate }) {
  const { data = null, isLoading: loading } = useQuery({
    queryKey: queryKeys.utilization.bySite(siteId, startDate, endDate),
    queryFn: () => {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return Api.post(`${SITE_UTILIZATION_URL}/${siteId}/utilization`, { startDate, endDate, timezone: tz });
    },
    enabled: Boolean(siteId && startDate && endDate),
    staleTime: 5 * 60 * 1000,
  });

  if (loading) {
    return (
      <div className="a-site__util a-site__util--loading">
        <Loader2 size={12} className="a-station__util-spinner" /> Loading...
      </div>
    );
  }
  if (!data) return null;

  const kpis = [
    { label: "Sessions", value: data.totalSessions ?? "—" },
    { label: "Utilization", value: data.utilizationPercent != null ? `${data.utilizationPercent}%` : "—" },
    { label: "Cost", value: data.totalSessionsCost != null ? `$${data.totalSessionsCost}` : "—" },
    { label: "Total Time", value: data.totalUtilizationTimeFormatted || "—" },
  ];

  return (
    <div className="a-site__util-kpis">
      {kpis.map(kpi => (
        <div key={kpi.label} className="a-site__util-kpi">
          <div className="a-site__kpi-val">{kpi.value}</div>
          <div className="a-site__kpi-lbl">{kpi.label}</div>
        </div>
      ))}
    </div>
  );
}

SiteUtilizationKpis.propTypes = {
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};

/* ══════════════════════════════════════════════════════════════
   Charger Tile — serial, displayName, online status, fw; clickable
   ══════════════════════════════════════════════════════════════ */
const STATUS_LABEL = {
  charging: "Charging",
  "plugged-in": "Plugged In",
  "not-plugged-in": "Not Plugged In",
  offline: "Offline",
};

function resolveChargerDisplayStatus(charger, fleetChargers) {
  if (!charger.isConnected) return "offline";
  const serial = charger.serialNumber || charger.chargerId || charger.id;
  const fleetCharger = fleetChargers?.find(c => c.serialNumber === serial);
  const connector = fleetCharger?.connectors?.[0];
  const isPluggedIn = connector?.pluggedIn === true;
  const isCharging = isPluggedIn && connector?.connectorStatusType === "CHARGING";
  if (isCharging) return "charging";
  if (isPluggedIn) return "plugged-in";
  return "not-plugged-in";
}

function ChargerTile({ charger, siteId }) {
  const navigate = useNavigate();
  const { setState: setGlobalFilterState } = useGlobalFilters();
  const { state: fleetState } = useFleetData() || { state: null };

  const serial = charger.serialNumber || charger.chargerId || charger.id || "—";
  const chargerName = charger.displayName || charger.chargerName || charger.name || charger.modelName || "";
  const status = resolveChargerDisplayStatus(charger, fleetState?.data);

  const handleClick = () => {
    setGlobalFilterState({ serialNumber: serial, siteId: String(siteId), isSerialNumberConfirmed: true });
    navigate(`/chargers/${serial}`);
  };

  return (
    <div
      className="a-charger a-charger--clickable"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === "Enter") handleClick();
      }}
    >
      <div className="a-charger__row">
        <div className={`a-charger__icon a-charger__icon--${status}`}>
          <BatteryCharging size={13} />
        </div>
        <span className="a-charger__name">{chargerName || serial}</span>
        <ExternalLink size={11} className="a-charger__link-icon" />
      </div>
      <div className="a-charger__serial">{serial}</div>
      <span className={`a-charger__status-text a-charger__status-text--${status}`}>{STATUS_LABEL[status]}</span>
    </div>
  );
}

ChargerTile.propTypes = {
  charger: PropTypes.object.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
